import { FC, ComponentProps, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Tooltip from 'react-tooltip-lite';
import { ReactSVG } from 'react-svg';

import { i18n as I18n } from 'utils/i18n';

import SelectList from '../selectList';

import useIsMobile from '../../../hooks/useIsMobile';

import {
  MOBILE_TOOLTIP_DISTANCE,
  DESKTOP_TOOLTIP_DISTANCE,
} from '../../../constants/breakpoints';

interface ClientService {
  clinical: boolean;
  name: string;
  id: number;
}

interface Props extends ComponentProps<typeof SelectList> {
  services: ClientService[];
  clinical?: boolean;
  showError?: boolean;
  autoSelect?: boolean;
  toolTip?: string;
}

const noOp = () => {};

const ServiceSelect: FC<Props> = ({
  services = [],
  clinical = false,
  loading = false,
  showError = true,
  autoSelect = false,
  onChange: setValue = noOp,
  toolTip = '',
  ...props
}) => {
  const options = services
    .filter(service => !(clinical && !service.clinical))
    .map(service => ({
      label: service.name,
      value: service.id,
    }));

  const [{ label: singleLabel, value: singleValue } = {}] =
    options.length === 1 ? options : [];

  const setValueRef = useRef(setValue);

  const isMobileView = useIsMobile();

  useEffect(() => {
    setValueRef.current = setValue;
  });

  useEffect(() => {
    if (autoSelect && singleValue) setValueRef.current(singleValue);
  }, [autoSelect, singleValue]);

  const { meta = {} } = props;

  const classes = classNames('field', 'field--info-only', {
    'field--has-error': !!meta.error,
  });

  return singleLabel ? (
    <div className={classes} data-testid="serviceTypeSingle">
      <div className="field__title">{I18n.t('shared.service')}</div>
      <div className="field__desc">{singleLabel}</div>
      {toolTip && (
        <Tooltip
          content={toolTip}
          className="tooltip"
          distance={
            isMobileView ? MOBILE_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
          }
        >
          <ReactSVG
            src="/images/icons/blue-question-mark.svg"
            width={17}
            height={17}
          />
        </Tooltip>
      )}
    </div>
  ) : (
    <SelectList
      label={I18n.t('shared.service')}
      placeholder={I18n.t('shared.service')}
      name="service"
      testid="serviceTypeMultiple"
      options={options}
      onChange={setValue}
      {...props}
      loading={loading || !services.length}
      error={!!meta.error}
      errorMsg={showError ? meta.error : undefined}
    />
  );
};

export default ServiceSelect;
